import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { PaymentService } from "domains/order/services/paymentService";
import { NfceService } from "domains/order/services/NfceService";
import {
	IPendingPixDeps,
	ProcessPendingPixUseCase,
	SendPendingPixUseCase,
} from "modules/payment/application/pix/ProcessPendingPixUseCase";
import { PENDING_PIX_INTERVAL_MS } from "modules/payment/application/pix/pendingPixPolicy";
import { getPendingPixList, IPendingPix } from "modules/payment/application/pix/pendingPixQueue";
import { normalizePixResponse } from "modules/payment/application/pix/pixPayload";
import { PixPaymentResponseRaw } from "modules/payment/domain/models/PixApprovedTransaction";
import { FC, useEffect, useRef } from "react";
import { GetError } from "utils/GetError";

export const createPendingPixDeps = (addLog?: (log: string, data: any) => void): IPendingPixDeps => ({
	getPixStatus: async (pixId) => normalizePixResponse((await PaymentService().pixPaymentStatus(pixId)) as PixPaymentResponseRaw),
	orderRepository: MeepOrderRepository(addLog),
	cashlessRepository: CashlessRepository(),
	nfceService: NfceService(),
	addOrderLog: async (order, message, pixId) => {
		// Transições da fila também vão para o App Insights
		try {
			addLog?.("pix", { message, orderId: order?.id, pixId });
		} catch {
			// ignora
		}
		await getOrderRepository().add(order as any, message);
	},
	now: () => Date.now(),
});

export const runPendingPixNow = async (kiosk: Kiosk, addLog?: (log: string, data: any) => void): Promise<void> => {
	try {
		await ProcessPendingPixUseCase(createPendingPixDeps(addLog), kiosk);
	} catch (error) {
		GetError(error, "runPendingPixNow", "PendingPixVerifier");
	}
};

export const sendPendingPixNow = async (
	pixId: string,
	kiosk: Kiosk,
	addLog?: (log: string, data: any) => void
): Promise<IPendingPix | undefined> => {
	const item = getPendingPixList().find((it) => it.pixId === pixId);
	if (!item) {
		return undefined;
	}
	return SendPendingPixUseCase(item, createPendingPixDeps(addLog), kiosk);
};

// Sem UI: confirma em segundo plano os PIX pendentes a cada 15 s
export const PendingPixVerifier: FC = () => {
	const { kiosk } = useKiosk();
	const { addLog } = useAppInsights();
	const kioskRef = useRef<Kiosk | null>(kiosk);
	const addLogRef = useRef(addLog);
	kioskRef.current = kiosk;
	addLogRef.current = addLog;

	const localId = kiosk?.localId;
	const hasKiosk = !!kiosk;

	useEffect(() => {
		if (!hasKiosk) {
			return;
		}
		const run = () => {
			if (kioskRef.current) {
				runPendingPixNow(kioskRef.current, addLogRef.current);
			}
		};
		run();
		const interval = setInterval(run, PENDING_PIX_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [hasKiosk, localId]);

	return null;
};

export default PendingPixVerifier;
